/**
 * Scheduled Jobs Storage Service
 *
 * Manages scheduled jobs and execution history in localStorage.
 * Phase 1: localStorage (mock)
 * Phase 2: Real API calls
 */

import { ScheduledJob, JobExecution } from '../types/scheduledJobs';

const STORAGE_KEY_JOBS = 'crm_scheduled_jobs';
const STORAGE_KEY_EXECUTIONS = 'crm_job_executions';

class ScheduledJobsStorage {
  /**
   * Get all scheduled jobs
   */
  getAllJobs(): ScheduledJob[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_JOBS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading scheduled jobs:', error);
      return [];
    }
  }

  /**
   * Get a single job by ID
   */
  getJob(jobId: string): ScheduledJob | null {
    const jobs = this.getAllJobs();
    return jobs.find(job => job.id === jobId) || null;
  }

  /**
   * Save a new job
   */
  saveJob(job: ScheduledJob): ScheduledJob {
    const jobs = this.getAllJobs();
    jobs.push(job);
    localStorage.setItem(STORAGE_KEY_JOBS, JSON.stringify(jobs));
    return job;
  }

  /**
   * Update an existing job
   */
  updateJob(jobId: string, updates: Partial<ScheduledJob>): ScheduledJob | null {
    const jobs = this.getAllJobs();
    const index = jobs.findIndex(job => job.id === jobId);

    if (index === -1) return null;

    jobs[index] = {
      ...jobs[index],
      ...updates,
      lastModifiedDate: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY_JOBS, JSON.stringify(jobs));
    return jobs[index];
  }

  /**
   * Delete a job
   */
  deleteJob(jobId: string): boolean {
    const jobs = this.getAllJobs();
    const filtered = jobs.filter(job => job.id !== jobId);

    if (filtered.length === jobs.length) return false;

    localStorage.setItem(STORAGE_KEY_JOBS, JSON.stringify(filtered));
    return true;
  }

  /**
   * Toggle job active status
   */
  toggleJobStatus(jobId: string): ScheduledJob | null {
    const job = this.getJob(jobId);
    if (!job) return null;

    return this.updateJob(jobId, {
      isActive: !job.isActive,
      status: !job.isActive ? 'scheduled' : 'paused'
    });
  }

  /**
   * Get all job executions
   */
  getAllExecutions(): JobExecution[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY_EXECUTIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading job executions:', error);
      return [];
    }
  }

  /**
   * Get executions for a specific job
   */
  getJobExecutions(jobId: string): JobExecution[] {
    const executions = this.getAllExecutions();
    return executions.filter(exec => exec.jobId === jobId);
  }

  /**
   * Save a job execution
   */
  saveExecution(execution: JobExecution): JobExecution {
    const executions = this.getAllExecutions();
    executions.unshift(execution); // Add to beginning (most recent first)

    // Keep only last 1000 executions
    if (executions.length > 1000) {
      executions.splice(1000);
    }

    localStorage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(executions));
    return execution;
  }

  /**
   * Update execution status
   */
  updateExecution(executionId: string, updates: Partial<JobExecution>): JobExecution | null {
    const executions = this.getAllExecutions();
    const index = executions.findIndex(exec => exec.id === executionId);

    if (index === -1) return null;

    executions[index] = { ...executions[index], ...updates };
    localStorage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(executions));
    return executions[index];
  }

  /**
   * Calculate next run time based on schedule
   */
  calculateNextRun(schedule: ScheduledJob['schedule']): string {
    const now = new Date();

    switch (schedule.frequency) {
      case 'once':
        return now.toISOString(); // Run immediately

      case 'daily':
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (schedule.time) {
          const [hours, minutes] = schedule.time.split(':');
          tomorrow.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }
        return tomorrow.toISOString();

      case 'weekly':
        const nextWeek = new Date(now);
        nextWeek.setDate(nextWeek.getDate() + 7);
        if (schedule.time) {
          const [hours, minutes] = schedule.time.split(':');
          nextWeek.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }
        return nextWeek.toISOString();

      case 'monthly':
        const nextMonth = new Date(now);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        if (schedule.dayOfMonth) {
          nextMonth.setDate(schedule.dayOfMonth);
        }
        if (schedule.time) {
          const [hours, minutes] = schedule.time.split(':');
          nextMonth.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        }
        return nextMonth.toISOString();

      case 'custom':
        // For custom cron, return tomorrow as placeholder
        const next = new Date(now);
        next.setDate(next.getDate() + 1);
        return next.toISOString();

      default:
        return now.toISOString();
    }
  }

  /**
   * Initialize with mock data if empty
   */
  initializeWithMockData(jobs: ScheduledJob[], executions: JobExecution[]): void {
    if (this.getAllJobs().length === 0) {
      localStorage.setItem(STORAGE_KEY_JOBS, JSON.stringify(jobs));
    }
    if (this.getAllExecutions().length === 0) {
      localStorage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(executions));
    }
  }

  /**
   * Reset to mock data
   */
  resetToMockData(jobs: ScheduledJob[], executions: JobExecution[]): void {
    localStorage.setItem(STORAGE_KEY_JOBS, JSON.stringify(jobs));
    localStorage.setItem(STORAGE_KEY_EXECUTIONS, JSON.stringify(executions));
  }
}

export const scheduledJobsStorage = new ScheduledJobsStorage();
